import { Component, ErrorInfo, ReactNode } from "react";
import { reportError } from "@/lib/monitoring";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary. Without one, an uncaught render error anywhere in
 * the tree unmounts the whole SPA to a blank white screen (React's default).
 * This catches it and shows a recoverable fallback instead.
 *
 * Kept deliberately dependency-free (only React, plain <a>/<button>, inline
 * Tailwind) so the boundary can't itself fail on the same error that tripped
 * it — e.g. a broken router or theme provider higher up.
 *
 * Limitation (React): boundaries only catch errors thrown during *render*.
 * Errors in event handlers, setTimeout, or rejected promises are NOT caught —
 * handle those at the call site (try/catch, handleApiError). This is the
 * white-screen safety net for render crashes, not a global error trap.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Funnel through the monitoring facade — the single seam an error monitor
    // plugs into once a DSN is configured (see lib/monitoring.ts).
    reportError(error, {
      source: "react-error-boundary",
      componentStack: info.componentStack,
    });
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center p-6 text-center"
      >
        <div className="w-full max-w-md">
          <h1 className="mb-3 text-2xl font-bold text-gray-800 dark:text-white/90">
            Something went wrong
          </h1>
          <p className="mb-8 text-base text-gray-600 dark:text-gray-400">
            The app hit an unexpected error. Reloading usually fixes it. If it
            keeps happening, please let us know what you were doing.
          </p>

          {import.meta.env.DEV && this.state.error && (
            <pre className="mb-8 max-h-48 overflow-auto rounded-lg bg-gray-100 p-4 text-left text-xs text-error-600 dark:bg-gray-800 dark:text-error-400">
              {this.state.error.message}
            </pre>
          )}

          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-lg bg-gray-900 dark:bg-white px-5 py-3.5 text-sm font-medium text-white dark:text-gray-900 shadow-theme-xs transition hover:bg-gray-800 dark:hover:bg-gray-100"
            >
              Reload page
            </button>
            {/* Hard navigation (not react-router) in case the router itself is what broke. */}
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3.5 text-sm font-medium text-gray-700 shadow-theme-xs transition hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
            >
              Go to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
